/**
 * 任务树看法（docs-html/prd/task-splitting.html 第三期）：把 GET /api/voko/task-forest 平铺的任务按 parent_task_id 连成树。
 * 子任务按发布者分成几支（擂台里不同参赛者各自拆的分开画）；连线已完成的画实线、进行中的画虚线。
 */

/** 连线画实线的状态：已经验收（质保中也算交付了）。 */
const DONE = ['settled', 'warranty']

/**
 * @typedef {{ id: string, title: string, status: string, template: string, reward: number, parent_task_id: string|null, has_children: boolean,
 *   tree_node: { slug: string, node_id: string }|null, publisher: { agent_id: string, name: string },
 *   holder: { agent_id: string, name: string }|null, entrants: Array<{ agent_id: string, name: string }>, created_at: string }} ForestTask
 * @typedef {{ publisher: { agent_id: string, name: string }, tasks: ForestTask[] }} Branch 同一个发布者在一个任务下面发的子任务
 */

/**
 * @param {ForestTask} task
 * @returns {'solid'|'dashed'}
 */
export function edgeOf(task) {
  return DONE.includes(task.status) ? 'solid' : 'dashed'
}

/**
 * @param {ForestTask[]} tasks 按创建时间从早到晚
 * @returns {{ branchesOf: (task_id: string) => Branch[], rootsUnder: (slug: string, node_id: string) => ForestTask[], loose: ForestTask[] }}
 *   `loose` 是没挂在任何树节点下的顶层任务；加载子页时必须同时保留已经加载的祖先
 */
export function buildForest(tasks) {
  const ids = new Set(tasks.map((task) => task.id))
  /** @type {Map<string, Branch[]>} */
  const branches = new Map()
  /** @type {Map<string, ForestTask[]>} */
  const attached = new Map()
  const loose = []
  for (const task of tasks.toSorted((left, right) => left.created_at.localeCompare(right.created_at) || left.id.localeCompare(right.id))) {
    if (task.parent_task_id !== null) {
      if (!ids.has(task.parent_task_id)) throw new Error(`Missing forest parent ${task.parent_task_id}, task: ${task.id}`)
      const list = branches.get(task.parent_task_id) ?? []
      const branch = list.find((item) => item.publisher.agent_id === task.publisher.agent_id)
      if (branch === undefined) list.push({ publisher: task.publisher, tasks: [task] })
      else branch.tasks.push(task)
      branches.set(task.parent_task_id, list)
    } else if (task.tree_node !== null) {
      const key = `${task.tree_node.slug}:${task.tree_node.node_id}`
      attached.set(key, [...(attached.get(key) ?? []), task])
    } else {
      loose.push(task)
    }
  }
  return {
    branchesOf: (task_id) => branches.get(task_id) ?? [],
    rootsUnder: (slug, node_id) => attached.get(`${slug}:${node_id}`) ?? [],
    loose,
  }
}
